import type { F0SelectItemProps } from '@factorialco/f0-react'
import { Money, Pencil, Timer } from '@factorialco/f0-react/icons/app'
import type {
  PayrollConceptSelectFieldFragment,
  PayrollValueObjectsConceptUnitTypeEnum,
} from 'generated/resources/graphql'

import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import type { PayrollConceptWithCategory } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import {
  mapAmountAndUpperLimitFromCents,
  mapAmountAndUpperLimitToCents,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationForm/AdditionalCompensationFormUtils'
import type { InMemoryAdditionalCompensation } from 'modules/contracts/lib/contractFlowContext'
import { parseDateString } from 'modules/contracts/lib/utils'

import {
  type AdditionalCompensationWizardAmountStrategyType,
  type AdditionalCompensationWizardInitialValues,
  type BasicStepValues,
  type DefinitionStepValues,
  type RawDefinitionStepValues,
  FIXED_AMOUNT_STRATEGY,
  PER_WORKED_DAY_STRATEGY,
  RECURRENCE_VALUES,
  SHOW_PENDING_PER_WORKED_DAY_FIELDS,
  VARIABLE_AMOUNT_STRATEGY,
  isSupportedAmountStrategyType,
  normalizeDefinitionNumber,
} from './additionalCompensationWizard.shared'

type DefinitionSectionCopy = {
  title: string
  description?: string
}

const normalizeDefinitionValues = (
  definitionValues: RawDefinitionStepValues
): DefinitionStepValues => {
  const fixedAmount = normalizeDefinitionNumber(definitionValues.fixedAmount)
  const maximumAmountPerCycle = normalizeDefinitionNumber(definitionValues.maximumAmountPerCycle)

  return {
    fixedAmount: fixedAmount == null ? null : fixedAmount,
    maximumAmountPerCycle: maximumAmountPerCycle == null ? null : maximumAmountPerCycle,
    pendingPerWorkedDayFields: definitionValues.pendingPerWorkedDayFields,
  }
}

const getDefinitionValuesFromAdditionalCompensation = (
  additionalCompensation: InMemoryAdditionalCompensation
): Partial<DefinitionStepValues> => {
  const normalizedAdditionalCompensation = mapAmountAndUpperLimitFromCents(additionalCompensation)
  const selectedUnitType = normalizedAdditionalCompensation.payrollConcept?.unitType

  return {
    fixedAmount: toDurationInitialValue(
      normalizedAdditionalCompensation.amountStrategy?.amount,
      selectedUnitType
    ),
    maximumAmountPerCycle:
      normalizedAdditionalCompensation.amountStrategy &&
      'upperLimit' in normalizedAdditionalCompensation.amountStrategy
        ? toDurationInitialValue(
            normalizedAdditionalCompensation.amountStrategy.upperLimit,
            selectedUnitType
          )
        : undefined,
  }
}

const getSelectedPayrollConcept = ({
  payrollConcepts,
  payrollConceptId,
}: {
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  payrollConceptId?: string
}) => payrollConcepts.find((payrollConcept) => payrollConcept.id === payrollConceptId)

const buildAdditionalCompensation = ({
  basicValues,
  definitionValues,
  contractVersionId,
  payrollConcepts,
  createTemporaryId,
  additionalCompensation,
}: {
  basicValues: BasicStepValues
  definitionValues: DefinitionStepValues
  contractVersionId: string
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  createTemporaryId: () => string
  additionalCompensation?: InMemoryAdditionalCompensation
}): InMemoryAdditionalCompensation => {
  if (!isSupportedAmountStrategyType(basicValues.amountStrategyType)) {
    throw new Error('Per-worked-day additional compensations are not supported yet')
  }

  const selectedPayrollConcept = getSelectedPayrollConcept({
    payrollConcepts,
    payrollConceptId: basicValues.payrollConceptId,
  })

  const amountStrategy =
    basicValues.amountStrategyType === FIXED_AMOUNT_STRATEGY
      ? {
          __typename: 'ContractsFixedAmountStrategy' as const,
          amount: definitionValues.fixedAmount ?? null,
        }
      : {
          __typename: 'ContractsVariableAmountStrategy' as const,
          amount: null,
          upperLimit: definitionValues.maximumAmountPerCycle ?? null,
        }

  const payrollConcept = selectedPayrollConcept
    ? {
        __typename: 'PayrollConcept' as const,
        id: selectedPayrollConcept.id,
        label: selectedPayrollConcept.label,
        name: selectedPayrollConcept.name,
        unitType: selectedPayrollConcept.unitType,
      }
    : (additionalCompensation?.payrollConcept ?? null)

  const baseAdditionalCompensation: Omit<InMemoryAdditionalCompensation, 'id' | 'tmpID'> = {
    __typename: 'ContractsAdditionalCompensation',
    contractVersionId: additionalCompensation?.contractVersionId ?? contractVersionId,
    description: additionalCompensation?.description ?? null,
    amountStrategyType: basicValues.amountStrategyType,
    amountStrategy,
    payrollConceptId: basicValues.payrollConceptId,
    payrollConcept,
    recurrence: basicValues.recurrence,
    firstPaymentOn: i18nDateToString(basicValues.firstPaymentOn, 'backend'),
  }

  if (!additionalCompensation) {
    return {
      ...baseAdditionalCompensation,
      tmpID: createTemporaryId(),
    }
  }

  if (additionalCompensation.id && additionalCompensation.tmpID) {
    return {
      ...baseAdditionalCompensation,
      id: additionalCompensation.id,
      tmpID: additionalCompensation.tmpID,
    }
  }

  if (additionalCompensation.id) {
    return {
      ...baseAdditionalCompensation,
      id: additionalCompensation.id,
    }
  }

  return {
    ...baseAdditionalCompensation,
    tmpID: additionalCompensation.tmpID ?? createTemporaryId(),
  }
}

const getDefinitionSectionCopy = (
  amountStrategyType?: AdditionalCompensationWizardAmountStrategyType
): DefinitionSectionCopy => {
  if (amountStrategyType === FIXED_AMOUNT_STRATEGY) {
    return {
      title: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.title'
      ),
      description: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.description'
      ),
    }
  }

  if (amountStrategyType === VARIABLE_AMOUNT_STRATEGY) {
    return {
      title: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.title'
      ),
      description: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.description'
      ),
    }
  }

  if (amountStrategyType === PER_WORKED_DAY_STRATEGY) {
    return {
      title: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.title'
      ),
      description: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.description'
      ),
    }
  }

  return {
    title: i18n.t(
      'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.title'
    ),
  }
}

const buildRecurrenceOptions = (): F0SelectItemProps<(typeof RECURRENCE_VALUES)[number]>[] => [
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.monthly_option'
    ),
    value: 'monthly',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_2_months_option'
    ),
    value: 'every_2_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_3_months_option'
    ),
    value: 'every_3_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_4_months_option'
    ),
    value: 'every_4_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_5_months_option'
    ),
    value: 'every_5_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_6_months_option'
    ),
    value: 'every_6_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_7_months_option'
    ),
    value: 'every_7_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_8_months_option'
    ),
    value: 'every_8_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_9_months_option'
    ),
    value: 'every_9_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_10_months_option'
    ),
    value: 'every_10_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_11_months_option'
    ),
    value: 'every_11_months',
  },
  {
    label: i18n.t(
      'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_12_months_option'
    ),
    value: 'every_12_months',
  },
]

const buildAmountStrategyTypeOptions = (): Array<
  F0SelectItemProps<AdditionalCompensationWizardAmountStrategyType>
> => {
  const options: Array<F0SelectItemProps<AdditionalCompensationWizardAmountStrategyType>> = [
    {
      value: FIXED_AMOUNT_STRATEGY,
      label: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.fixed_amount.label'
      ),
      description: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.fixed_amount.description'
      ),
      icon: Money,
    },
    {
      value: VARIABLE_AMOUNT_STRATEGY,
      label: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.manual_entry_each_cycle.label'
      ),
      description: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.manual_entry_each_cycle.description'
      ),
      icon: Pencil,
    },
  ]

  if (SHOW_PENDING_PER_WORKED_DAY_FIELDS) {
    options.push({
      value: PER_WORKED_DAY_STRATEGY,
      label: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.per_worked_day.label'
      ),
      description: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.per_worked_day.description'
      ),
      icon: Timer,
    })
  }

  return options
}

const shouldShowCompensationExcludedFromFirstPeriodCallout = ({
  firstPaymentOn,
  effectiveOn,
}: {
  firstPaymentOn: unknown
  effectiveOn?: string
}) => {
  if (!(firstPaymentOn instanceof Date) || !effectiveOn) return false

  const effectiveDate = parseDateString(effectiveOn)
  if (!effectiveDate?.isValid()) return false

  const isNotFirstDay = effectiveDate.date() !== 1
  if (!isNotFirstDay) return false

  return (
    firstPaymentOn.getFullYear() === effectiveDate.year() &&
    firstPaymentOn.getMonth() === effectiveDate.month()
  )
}

const createBasicContextFromValues = (values: BasicStepValues) => ({
  amountStrategyType: values.amountStrategyType,
  payrollConceptId: values.payrollConceptId,
})

const toDurationInitialValue = (
  value: number | null | undefined,
  unitType?: PayrollValueObjectsConceptUnitTypeEnum | null
) => {
  if (value == null) {
    return undefined
  }

  return unitType === 'time' ? value * 60 : value
}

const buildWizardInitialValues = ({
  additionalCompensation,
  defaultFirstPaymentDate,
}: {
  additionalCompensation?: InMemoryAdditionalCompensation
  defaultFirstPaymentDate?: Date
}): AdditionalCompensationWizardInitialValues | undefined => {
  if (!additionalCompensation) {
    return undefined
  }

  const parsedFirstPaymentOn = additionalCompensation.firstPaymentOn
    ? parseDateString(additionalCompensation.firstPaymentOn)
    : null

  return {
    basic: {
      payrollConceptId: additionalCompensation.payrollConceptId,
      amountStrategyType: additionalCompensation.amountStrategyType,
      recurrence:
        RECURRENCE_VALUES.find((recurrence) => recurrence === additionalCompensation.recurrence) ??
        'monthly',
      firstPaymentOn: parsedFirstPaymentOn?.isValid()
        ? parsedFirstPaymentOn.toDate()
        : defaultFirstPaymentDate,
    },
    definition: getDefinitionValuesFromAdditionalCompensation(additionalCompensation),
  }
}

const buildMappedAdditionalCompensation = (
  params: Parameters<typeof buildAdditionalCompensation>[0]
) => mapAmountAndUpperLimitToCents(buildAdditionalCompensation(params))

export {
  buildAdditionalCompensation,
  buildAmountStrategyTypeOptions,
  buildMappedAdditionalCompensation,
  buildRecurrenceOptions,
  buildWizardInitialValues,
  createBasicContextFromValues,
  getDefinitionValuesFromAdditionalCompensation,
  getDefinitionSectionCopy,
  getSelectedPayrollConcept,
  normalizeDefinitionValues,
  shouldShowCompensationExcludedFromFirstPeriodCallout,
}

export type { PayrollConceptWithCategory }
